import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { admin } from '../../lib/api';
import type { Game, VideoClip } from '../../types';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Heading } from '../../ui/heading';
import { Select } from '../../ui/select';
import { Text } from '../../ui/text';
import { EmptyState, Panel } from '../../components/PageChrome';

export default function Clips() {
  const { teamId } = useParams<{ teamId: string }>();
  const [games, setGames] = useState<Game[]>([]);
  const [gameId, setGameId] = useState('');
  const [clips, setClips] = useState<VideoClip[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!teamId) return;
    void (async () => {
      try {
        const res = await admin.listGames(teamId);
        setGames(res.games);
        const first = res.games.find((g) => g.status === 'READY' || g.status === 'READY_FOR_REVIEW') ?? res.games[0];
        if (first) setGameId(first.gameId);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, [teamId]);

  useEffect(() => {
    if (!gameId) return;
    void (async () => {
      try {
        const res = await admin.listClips(gameId);
        setClips(res.clips);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, [gameId]);

  const generate = async () => {
    if (!teamId || !gameId) return;
    setBusy(true);
    setError(undefined);
    try {
      const res = await admin.generateClips(teamId, gameId);
      setClips(res.clips);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const game = games.find((g) => g.gameId === gameId);

  return (
    <div className="space-y-6">
      <div>
        <Heading level={2}>Clips</Heading>
        <Text className="mt-1 text-sm">Generate highlight windows from scorebook events with video timestamps.</Text>
      </div>
      <Panel className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0 flex-1">
          <Text className="mb-2 text-xs">Game</Text>
          <Select value={gameId} onChange={(e) => setGameId(e.target.value)}>
            {games.map((g) => <option key={g.gameId} value={g.gameId}>{new Date(g.scheduledAt).toLocaleDateString()} · {g.opponent}</option>)}
          </Select>
        </div>
        <Button color="dark/zinc" onClick={() => void generate()} disabled={!gameId || busy}>{busy ? 'Generating…' : 'Generate clips'}</Button>
      </Panel>
      {game ? <Text className="text-sm">Selected: {game.homeOrAway === 'AWAY' ? '@' : 'vs'} {game.opponent}</Text> : null}
      {error ? <Text className="text-red-400">{error}</Text> : null}
      {clips.length === 0 ? (
        <EmptyState title="No clips yet" body="Scorebook highlight events will create clip windows here." />
      ) : (
        <ul className="grid gap-3 lg:grid-cols-2">
          {clips.map((clip) => (
            <li key={clip.clipId} className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium text-zinc-100">{clip.title}</div>
                  <Text className="mt-1 text-sm">{clip.startSec}s to {clip.endSec}s{clip.playType ? ` · ${clip.playType}` : ''}</Text>
                </div>
                <Badge color={clip.status === 'READY' ? 'lime' : clip.status === 'FAILED' ? 'rose' : 'amber'}>{clip.status}</Badge>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
